import React, { useState } from 'react';
import { RulerIcon, SquareIcon, RefreshCwIcon } from 'lucide-react';
interface InfoTabProps {
  totalLength: number;
  calculatedArea: number;
}
export function InfoTab({
  totalLength,
  calculatedArea
}: InfoTabProps) {
  const [targetRadius, setTargetRadius] = useState(totalLength.toFixed(2));
  const [showRadiusOnMap, setShowRadiusOnMap] = useState(true);
  const handleUpdateFromSpans = () => {
    setTargetRadius(totalLength.toFixed(2));
  };
  const radiusDifference = Math.abs(parseFloat(targetRadius) - totalLength);
  const hasDiscrepancy = radiusDifference > 0.01;
  return <div className="space-y-6" data-id="element-100">
      {/* Real Dimensions Display - Now Featured at Top */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 shadow-lg" data-id="element-101">
        <div className="flex items-center gap-2 mb-4" data-id="element-102">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center" data-id="element-103">
            <RulerIcon className="w-5 h-5 text-white" data-id="element-104" />
          </div>
          <h3 className="text-lg font-bold text-white" data-id="element-105">Real Dimensions</h3>
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-106">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" data-id="element-107">
            <div className="flex items-center gap-2 mb-2" data-id="element-108">
              <RulerIcon className="w-4 h-4 text-blue-100" data-id="element-109" />
              <span className="text-xs font-medium text-blue-100 uppercase tracking-wide" data-id="element-110">
                Length
              </span>
            </div>
            <p className="text-2xl font-bold text-white" data-id="element-111">
              {totalLength.toFixed(2)}
            </p>
            <p className="text-sm text-blue-100" data-id="element-112">meters</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" data-id="element-113">
            <div className="flex items-center gap-2 mb-2" data-id="element-114">
              <SquareIcon className="w-4 h-4 text-blue-100" data-id="element-115" />
              <span className="text-xs font-medium text-blue-100 uppercase tracking-wide" data-id="element-116">
                Area
              </span>
            </div>
            <p className="text-2xl font-bold text-white" data-id="element-117">
              {calculatedArea.toFixed(2)}
            </p>
            <p className="text-sm text-blue-100" data-id="element-118">hectares</p>
          </div>
        </div>
      </div>
      {/* Divider */}
      <div className="relative" data-id="element-119">
        <div className="absolute inset-0 flex items-center" data-id="element-120">
          <div className="w-full border-t border-gray-200" data-id="element-121"></div>
        </div>
        <div className="relative flex justify-center text-sm" data-id="element-122">
          <span className="px-2 bg-white text-gray-500" data-id="element-123">Input Values</span>
        </div>
      </div>
      {/* Name Input */}
      <div data-id="element-124">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-125">
          <span className="text-red-500" data-id="element-126">*</span> Name
        </label>
        <input type="text" defaultValue="New pivot" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-127" />
      </div>
      {/* Target Radius Drawed Input */}
      <div data-id="element-128">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-129">
          <span className="text-red-500" data-id="element-130">*</span> Target radius drawn
        </label>
        <div className="flex gap-2" data-id="element-131">
          <input type="number" value={targetRadius} onChange={e => setTargetRadius(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-132" />
          <span className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium" data-id="element-133">
            m
          </span>
        </div>
        {hasDiscrepancy && <div className="mt-2 flex items-center gap-2" data-id="element-134">
            <button onClick={handleUpdateFromSpans} className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors" data-id="element-135">
              <RefreshCwIcon className="w-3 h-3" data-id="element-136" />
              Update from actual spans
            </button>
            <span className="text-xs text-amber-600" data-id="element-137">
              Difference: {radiusDifference.toFixed(2)}m
            </span>
          </div>}
        <p className="text-xs text-gray-500 mt-1" data-id="element-138">Target radius drawn on map</p>
      </div>
      {/* Target Area Input */}
      <div data-id="element-139">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-140">
          <span className="text-red-500" data-id="element-141">*</span> Target area
        </label>
        <div className="flex gap-2" data-id="element-142">
          <input type="number" value={calculatedArea.toFixed(2)} readOnly className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed" data-id="element-143" />
          <span className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium" data-id="element-144">
            ha
          </span>
        </div>
      </div>
      {/* Degrees Input */}
      <div data-id="element-145">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-146">
          <span className="text-red-500" data-id="element-147">*</span> Degrees
        </label>
        <div className="flex gap-2" data-id="element-148">
          <input type="number" defaultValue="360" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-149" />
          <span className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium" data-id="element-150">
            °
          </span>
        </div>
      </div>
      {/* Technical Sheet Checkbox */}
      <label className="flex items-center gap-2 text-gray-700 text-sm cursor-pointer pt-2" data-id="element-151">
        <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-152" />
        <span data-id="element-153">Show technical sheet</span>
      </label>
    </div>;
}